import { useCallback, useContext, useEffect, useState } from "react";
import { WebGLContext } from ".";
import { cleanProgram } from "./utils";
export const useShaders = (gl, vertexShader, fragmentShader) => {
    const [shaderProgram, setShaderProgram] = useState(null);
    useEffect(() => {
        if (!gl)
            return () => { };
        if (shaderProgram) {
            cleanProgram({ gl, shaderProgram });
        }
        const shaderProgramTemp = gl.createProgram();
        gl.attachShader(shaderProgramTemp, vertexShader);
        gl.attachShader(shaderProgramTemp, fragmentShader);
        gl.linkProgram(shaderProgramTemp);
        if (!gl.getProgramParameter(shaderProgramTemp, gl.LINK_STATUS)) {
            throw new Error("Unable to initialize the shader program: " +
                gl.getProgramInfoLog(shaderProgramTemp));
        }
        setShaderProgram(shaderProgramTemp);
        return () => cleanProgram({ gl, shaderProgram: shaderProgramTemp });
    }, [gl, vertexShader, fragmentShader]);
    return { shaderProgram };
};
export const useAttributes = (gl, shaderProgram, attributes, items, id, drawMode = gl.TRIANGLE_STRIP) => {
    const [attributesLocations, setAttributes] = useState(null);
    const { requestUpdate, registerUpdate, unregisterUpdate } = useContext(WebGLContext);
    useEffect(() => {
        if (shaderProgram) {
            gl.useProgram(shaderProgram);
            const attributesMap = {};
            attributes.forEach((attribute) => {
                const location = gl.getAttribLocation(shaderProgram, attribute.name);
                gl.enableVertexAttribArray(location);
                attributesMap[attribute.name] = location;
            });
            setAttributes(attributesMap);
        }
    }, [gl, shaderProgram, attributes]);
    useEffect(() => {
        if (!shaderProgram || !attributesLocations)
            return;
        gl.useProgram(shaderProgram);
        attributes.forEach((attribute) => {
            const location = attributesLocations[attribute.name];
            gl.bindBuffer(gl.ARRAY_BUFFER, attribute.buffer);
            gl.bufferData(gl.ARRAY_BUFFER, attribute.values, gl.DYNAMIC_DRAW);
            gl.vertexAttribPointer(location, attribute.index, gl.FLOAT, false, 0, 0);
        });
        requestUpdate();
    }, [shaderProgram, attributes, attributesLocations]);
    const drawProgram = useCallback(() => {
        if (!shaderProgram || !attributesLocations)
            return;
        gl.useProgram(shaderProgram);
        attributes.forEach((attribute) => {
            gl.bindBuffer(gl.ARRAY_BUFFER, attribute.buffer);
            gl.vertexAttribPointer(attributesLocations[attribute.name], attribute.index, gl.FLOAT, false, 0, 0);
        });
        gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
        gl.drawArrays(drawMode, 0, items);
    }, [shaderProgram, attributes, attributesLocations, items, drawMode]);
    useEffect(() => {
        unregisterUpdate(id);
        registerUpdate(id, drawProgram);
        return () => unregisterUpdate(id);
    }, [drawProgram, id]);
    return { attributes, drawProgram };
};
//# sourceMappingURL=hooks.js.map